// Per-report React error boundary.
//
// Wraps each individual report in Analytics so one crashing report
// (bad data shape, undefined field access, Mantine prop drift) doesn't
// take down the whole Analytics tree. Shows an inline Alert with the
// error message + a "retry" button that remounts the subtree.
//
// Why class component: React's native error boundaries require it;
// `componentDidCatch` + `getDerivedStateFromError` are not available
// as hooks.

import React from 'react';
import { Alert, Button, Code, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconReload } from '@tabler/icons-react';

export class ReportBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, remountKey: 0 };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to console for inspection in DevTools; don't ship to a backend —
    // the Analytics screen is already debugger-grade and we don't have a
    // telemetry sink wired here.
    // eslint-disable-next-line no-console
    console.error(`[ReportBoundary:${this.props.name ?? 'unknown'}] render failure`, error, info);
  }

  handleRetry() {
    this.setState((s) => ({ error: null, remountKey: s.remountKey + 1 }));
  }

  render() {
    if (!this.state.error) {
      // key bump forces full remount of the subtree on retry
      return <React.Fragment key={this.state.remountKey}>{this.props.children}</React.Fragment>;
    }
    const name = this.props.name ?? 'report';
    const msg = this.state.error?.message ?? String(this.state.error);
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />} title={`${name} crashed`}>
        <Stack gap="xs">
          <Text size="xs">
            This report hit a render error. The rest of Analytics is unaffected. Full stack is in the
            browser console under <Code>[ReportBoundary:{name}]</Code>.
          </Text>
          <Code block style={{ fontSize: 11 }}>{msg}</Code>
          <Group>
            <Button size="xs" leftSection={<IconReload size={12} />} onClick={this.handleRetry} variant="light">
              Retry this report
            </Button>
          </Group>
        </Stack>
      </Alert>
    );
  }
}
